import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { IconPlus } from '../components/icons';

interface ProjectRow {
  id: string;
  title: string;
  description: string | null;
  projectType: 'game' | 'app' | 'tool';
  status: 'draft' | 'published';
  visibility: 'private' | 'public';
  thumbnailUrl: string | null;
  updatedAt: string;
}

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'game', label: 'Games' },
  { key: 'app', label: 'Apps' },
  { key: 'tool', label: 'Tools' },
] as const;

/** Relative time, because "2 hours ago" is friendlier than a timestamp on a wall of cards. */
function ago(iso: string): string {
  const seconds = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return days < 7 ? `${days}d ago` : new Date(iso).toLocaleDateString();
}

/**
 * "My creations" — the old Code Box gallery: a responsive card grid with cover, status badges,
 * relative time and a single forward action per card.
 */
export function ProjectsPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<(typeof FILTERS)[number]['key']>('all');

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ projects: ProjectRow[] }>('/box/projects');
      setProjects(data.projects);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load your creations');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // No row is created here — Define owns that, so backing out of it leaves nothing behind.
  const startNewBuild = () => navigate('/projects/new');

  const visible = useMemo(() => {
    if (!projects) return null;
    const q = query.trim().toLowerCase();
    return projects.filter(
      p =>
        (filter === 'all' || p.projectType === filter) &&
        (!q || p.title.toLowerCase().includes(q) || (p.description ?? '').toLowerCase().includes(q)),
    );
  }, [projects, query, filter]);

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-8">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">My creations</h1>
          <p className="mt-1 text-sm text-muted">Everything you have built or been invited to.</p>
        </div>
        <button
          type="button"
          onClick={startNewBuild}
          className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-accent px-5 text-sm font-extrabold text-accent-ink transition hover:brightness-95"
        >
          <IconPlus />
          New build
        </button>
      </div>

      {projects && projects.length > 0 && (
        <div className="mb-5 flex flex-wrap items-center gap-2">
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search title…"
            className="h-11 min-w-[200px] flex-1 rounded-xl border border-line bg-surface px-3.5 text-sm outline-none ring-1 ring-transparent transition placeholder:text-muted focus:border-ink/50 focus:ring-ink/20"
          />
          <div className="flex gap-1 rounded-xl bg-page p-1">
            {FILTERS.map(f => {
              const count =
                f.key === 'all' ? projects.length : projects.filter(p => p.projectType === f.key).length;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setFilter(f.key)}
                  className={[
                    'rounded-lg px-3 py-2 text-sm font-semibold transition',
                    filter === f.key ? 'bg-surface text-ink shadow-sm' : 'text-muted hover:text-ink',
                  ].join(' ')}
                >
                  {f.label}
                  <span className="ml-1 text-xs text-muted">{count}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-2xl border border-line bg-surface p-3 text-sm text-muted">{error}</div>
      )}

      {projects === null && !error && (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map(i => (
            <li key={i} className="skeleton h-56 rounded-2xl" />
          ))}
        </ul>
      )}

      {visible?.length === 0 && projects?.length === 0 && (
        <div className="rounded-2xl border border-dashed border-line p-12 text-center">
          <p className="text-base font-bold">No creations yet</p>
          <p className="mt-1 text-sm text-muted">Start your first build — it takes a couple of minutes.</p>
          <button
            type="button"
            onClick={startNewBuild}
            className="mt-4 inline-flex h-11 items-center justify-center rounded-xl bg-accent px-5 text-sm font-extrabold text-accent-ink transition hover:brightness-95"
          >
            Start building →
          </button>
        </div>
      )}

      {visible?.length === 0 && (projects?.length ?? 0) > 0 && (
        <div className="rounded-2xl border border-dashed border-line p-12 text-center">
          <p className="text-base font-bold">No matches</p>
          <p className="mt-1 text-sm text-muted">Try a different search term or filter.</p>
          <button
            type="button"
            onClick={() => {
              setQuery('');
              setFilter('all');
            }}
            className="mt-4 inline-flex h-9 items-center justify-center rounded-xl border border-line px-3.5 text-[13px] font-semibold transition hover:bg-page"
          >
            Clear filters
          </button>
        </div>
      )}

      {visible && visible.length > 0 && (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map(project => (
            <li key={project.id}>
              <Link
                to={`/projects/${project.id}`}
                className="anim-in group flex h-full flex-col overflow-hidden rounded-2xl border border-line bg-surface transition hover:-translate-y-0.5 hover:border-accent/50 hover:shadow-card"
              >
                <div className="relative grid aspect-[16/10] place-items-center overflow-hidden bg-gradient-to-br from-accent/25 via-accent/10 to-transparent">
                  {project.thumbnailUrl ? (
                    <img
                      src={project.thumbnailUrl}
                      alt=""
                      className="absolute inset-0 h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
                    />
                  ) : (
                    <span className="text-3xl font-black text-accent-deep opacity-60">
                      {project.title.slice(0, 1).toUpperCase()}
                    </span>
                  )}
                  <div className="absolute left-2 top-2 flex gap-1">
                    <span
                      className={[
                        'rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wide backdrop-blur',
                        project.status === 'published'
                          ? 'bg-success/90 text-white'
                          : 'bg-ink/70 text-surface',
                      ].join(' ')}
                    >
                      {project.status === 'published'
                        ? project.visibility === 'public'
                          ? 'Published'
                          : 'Published · Link only'
                        : 'Draft'}
                    </span>
                    <span className="rounded-full bg-surface/85 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-accent-deep backdrop-blur">
                      {project.projectType}
                    </span>
                  </div>
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <p className="truncate font-extrabold">{project.title || '(untitled)'}</p>
                  <p className="mt-1 line-clamp-2 flex-1 text-sm text-muted">
                    {project.description ?? 'No description'}
                  </p>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-xs text-muted">{ago(project.updatedAt)}</span>
                    <span className="text-[13px] font-bold text-accent-deep group-hover:underline">
                      Open →
                    </span>
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
